import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { cn } from '../../lib/utils';
import {
  FileText, Edit, CheckCircle2, Search, Send, Eye, Sparkles, AlertTriangle,
  Copy, Stethoscope, Brain, ShieldCheck, ListChecks, UserCheck, Printer,
  ClipboardCopy, Link as LinkIcon, Clock, Mic, Upload, ArrowLeft, ArrowRight,
  Loader2, Wand2, BookCheck, Filter, CalendarDays, ChevronDown, SquareCheck, X,
  Camera, MessageSquare, Play, Volume2, Command, Zap, ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import NoteReviewLightbox from '../../components/shared/NoteReviewLightbox';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_NOTES = [
  { id: 'NOTE-001', patient: 'John Smith', patientId: 'P001', date: '2026-02-03', status: 'completed', type: 'Follow-up', signed: true, specialty: 'primary_care', provider: 'Dr. Sarah Chen' },
  { id: 'NOTE-002', patient: 'Maria Garcia', patientId: 'P002', date: '2026-02-03', status: 'draft', type: 'New Patient', signed: false, specialty: 'primary_care', provider: 'Dr. Sarah Chen' },
  { id: 'NOTE-003', patient: 'Robert Johnson', patientId: 'P003', date: '2026-02-02', status: 'review', type: 'Pain Mgmt', signed: false, specialty: 'pain_management', provider: 'Dr. James Wilson' },
  { id: 'NOTE-004', patient: 'Emily Davis', patientId: 'P004', date: '2026-02-02', status: 'finalized', type: 'Annual', signed: false, specialty: 'primary_care', provider: 'Dr. Sarah Chen' },
  { id: 'NOTE-005', patient: 'Sarah Williams', patientId: 'P005', date: '2026-02-01', status: 'draft', type: 'Follow-up', signed: false, specialty: 'cardiology', provider: 'Dr. Maria Santos' },
  { id: 'NOTE-006', patient: 'James Taylor', patientId: 'P006', date: '2026-02-01', status: 'completed', type: 'Sick Visit', signed: true, specialty: 'primary_care', provider: 'Dr. Sarah Chen' },
];

const REFERRAL_HISTORY = [
  { id: 'REF-001', patient: 'John Smith', specialty: 'Cardiology', date: '2026-02-01', status: 'sent' },
  { id: 'REF-002', patient: 'Emily Davis', specialty: 'Endocrinology', date: '2026-01-28', status: 'acknowledged' },
  { id: 'REF-003', patient: 'Maria Garcia', specialty: 'Ophthalmology', date: '2026-01-25', status: 'pending' },
];

const PRIOR_AUTH_HISTORY = [
  { id: 'PA-001', patient: 'Robert Johnson', procedure: 'MRI Lumbar Spine', date: '2026-02-02', status: 'approved' },
  { id: 'PA-002', patient: 'Sarah Williams', procedure: 'Echocardiogram', date: '2026-01-30', status: 'pending' },
  { id: 'PA-003', patient: 'John Smith', procedure: 'CT Abdomen', date: '2026-01-22', status: 'denied' },
];

const PROCEDURE_NOTE_HISTORY = [
  { id: 'PN-001', patient: 'Emily Davis', procedure: 'Joint Injection', date: '2026-02-03', status: 'completed' },
  { id: 'PN-002', patient: 'Robert Johnson', procedure: 'Skin Biopsy', date: '2026-01-29', status: 'completed' },
];

import { useAppSync } from '../../contexts/AppSyncContext';

const STATUS_BADGE = {
  sent: { label: 'Sent', cls: 'bg-blue-50 text-blue-700' },
  acknowledged: { label: 'Acknowledged', cls: 'bg-emerald-50 text-emerald-700' },
  pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700' },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700' },
  denied: { label: 'Denied', cls: 'bg-red-50 text-red-700' },
  completed: { label: 'Signed', cls: 'bg-emerald-100 text-emerald-700' },
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-700' },
  review: { label: 'Ready for Review', cls: 'bg-blue-100 text-blue-700' },
  finalized: { label: 'Finalized', cls: 'bg-slate-200 text-slate-700' },
};

const FILTER_TABS = [
  { key: 'soap', label: 'SOAP Notes', icon: FileText, desc: 'View, edit, and export clinical documentation' },
  { key: 'referral', label: 'Referral Letters', icon: Send, desc: 'Generate and manage patient referral letters' },
  { key: 'prior-auth', label: 'Prior Authorization', icon: ClipboardCopy, desc: 'Draft and track prior authorization requests' },
  { key: 'procedure', label: 'Procedure Notes', icon: Wand2, desc: 'Generate structured procedure documentation' },
  { key: 'capture', label: 'Capture Tools', icon: Mic, desc: 'Ambient listening, dictation, and Lens capture' },
  { key: 'sessions', label: 'Session History', icon: Clock, desc: 'Past recording sessions and generated notes' },
  { key: 'commands', label: 'Voice Commands', icon: Command, desc: 'Hands-free voice command reference' },
];

const REFERRAL_TEMPLATE = (specialty, reason) =>
`${new Date().toLocaleDateString()}\n\nRE: Referral for Patient\nSpecialty: ${specialty || 'Specialist'}\n\nDear Colleague,\n\nI am writing to refer this patient for ${specialty} evaluation regarding ${reason || 'further management'}.\n\nSincerely,\nDr. Sarah Chen, MD`;

const PRIOR_AUTH_TEMPLATE = (procedure, diagnosis) =>
`PRIOR AUTHORIZATION REQUEST\nDate: ${new Date().toLocaleDateString()}\n\nREQUESTING PROVIDER\nDr. Sarah Chen, MD | NPI: 1234567890\n\nREQUESTED SERVICE\nProcedure: ${procedure || 'N/A'}\nPrimary Diagnosis: ${diagnosis || 'N/A'}`;

const PROCEDURE_TEMPLATES = {
  'Joint Injection': `PROCEDURE NOTE\nProcedure: Intra-articular Joint Injection | CPT: 20610\n\nConsent: Risks, benefits, alternatives discussed.`,
  'Skin Biopsy': `PROCEDURE NOTE\nProcedure: Punch Biopsy of Skin Lesion | CPT: 11104\n\nConsent: Informed consent obtained.`,
  'Laceration Repair': `PROCEDURE NOTE\nProcedure: Simple Laceration Repair | CPT: 12001`,
};

const PROVIDERS = ['All Providers', 'Dr. Sarah Chen', 'Dr. James Wilson', 'Dr. Maria Santos'];

const RECORDING_HISTORY = [
  { id: 'REC-001', patient: 'John Smith', patientId: 'P001', type: 'Ambient', date: '2026-02-03 10:30 AM', duration: '18 min', status: 'note_generated', chief: 'Diabetes management follow-up', provider: 'Dr. Sarah Chen' },
  { id: 'REC-002', patient: 'Maria Garcia', patientId: 'P002', type: 'Ambient', date: '2026-02-03 11:00 AM', duration: '12 min', status: 'note_generated', chief: 'New patient intake', provider: 'Dr. Sarah Chen' },
  { id: 'REC-003', patient: 'Robert Johnson', patientId: 'P003', type: 'Dictation', date: '2026-02-03 11:45 AM', duration: '8 min', status: 'needs_review', chief: 'Acute cough follow-up', provider: 'Dr. James Wilson' },
  { id: 'REC-004', patient: 'Emily Davis', patientId: 'P004', type: 'Ambient', date: '2026-02-02 09:15 AM', duration: '22 min', status: 'note_generated', chief: 'Annual physical', provider: 'Dr. Sarah Chen' },
  { id: 'REC-005', patient: 'Sarah Williams', patientId: 'P005', type: 'Dictation', date: '2026-02-02 02:00 PM', duration: '15 min', status: 'needs_review', chief: 'Cardiology referral', provider: 'Dr. Maria Santos' },
];

const VOICE_COMMANDS = [
  { category: 'Navigation', commands: [
    { cmd: '"Lumina, next section"', desc: 'Move to the next SOAP section (S\u2192O\u2192A\u2192P)' },
    { cmd: '"Lumina, go to plan"', desc: 'Jump directly to the Plan section' },
    { cmd: '"Lumina, go back"', desc: 'Return to the previous section' },
  ]},
  { category: 'Documentation', commands: [
    { cmd: '"Lumina, mark that as assessment"', desc: 'Route the last spoken sentence to the Assessment section' },
    { cmd: '"Lumina, add to plan"', desc: 'Append the next dictated content to the Plan section' },
    { cmd: '"Lumina, new paragraph"', desc: 'Insert a paragraph break in the current section' },
    { cmd: '"Lumina, correction"', desc: 'Delete the last sentence and re-record' },
  ]},
  { category: 'Clinical Actions', commands: [
    { cmd: '"Lumina, order [lab/test name]"', desc: 'Create a pending order for the specified lab or test' },
    { cmd: '"Lumina, prescribe [medication]"', desc: 'Create a draft prescription for the named medication' },
    { cmd: '"Lumina, flag for review"', desc: 'Mark the current note for physician review before signing' },
    { cmd: '"Lumina, add diagnosis [condition]"', desc: 'Add a clinical diagnosis to the Assessment section' },
  ]},
  { category: 'Session Control', commands: [
    { cmd: '"Lumina, pause"', desc: 'Pause the recording session (private conversation)' },
    { cmd: '"Lumina, resume"', desc: 'Resume the paused recording session' },
    { cmd: '"Lumina, stop and generate"', desc: 'End the session and generate the SOAP note' },
    { cmd: '"Lumina, cancel session"', desc: 'Discard the current recording without generating a note' },
  ]},
];

const LuminaDocumentation = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('soap');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('All Providers');
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState(null);
  const [selectedNotes, setSelectedNotes] = useState(new Set());

  // MA mode detection
  const isMA = sessionStorage.getItem('lumina-role') === 'ma';
  const [showConfirmBatch, setShowConfirmBatch] = useState(null);
  // Generator states
  const [showGenerator, setShowGenerator] = useState(false);
  const [genSpecialty, setGenSpecialty] = useState('');
  const [genReason, setGenReason] = useState('');
  const [genProcedure, setGenProcedure] = useState('');
  const [genDiagnosis, setGenDiagnosis] = useState('');
  const [genTemplate, setGenTemplate] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  // Session history state
  const [sessionFilter, setSessionFilter] = useState('all');
  const [sessionSearch, setSessionSearch] = useState('');

  const filteredNotes = useMemo(() => {
    return DEMO_NOTES.filter(n => {
      const matchesSearch = n.patient.toLowerCase().includes(searchTerm.toLowerCase()) || n.type.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || n.status === statusFilter;
      const matchesProvider = providerFilter === 'All Providers' || n.provider === providerFilter;
      let matchesDate = true;
      if (dateRange.from) {
        const noteDate = new Date(n.date);
        matchesDate = noteDate >= dateRange.from;
        if (dateRange.to) matchesDate = matchesDate && noteDate <= dateRange.to;
      }
      return matchesSearch && matchesStatus && matchesProvider && matchesDate;
    });
  }, [searchTerm, statusFilter, providerFilter, dateRange]);

  const filteredSessions = useMemo(() => {
    return RECORDING_HISTORY.filter(r => {
      const matchesSearch = r.patient.toLowerCase().includes(sessionSearch.toLowerCase()) || r.chief.toLowerCase().includes(sessionSearch.toLowerCase());
      const matchesStatus = sessionFilter === 'all' || r.status === sessionFilter;
      return matchesSearch && matchesStatus;
    });
  }, [sessionSearch, sessionFilter]);

  const handleNewSession = (type) => {
    navigate(`/lumina/clinical-focus?patientId=P001&autoStart=${type}`);
  };

  // AI Workflow Stats
  const draftCount = DEMO_NOTES.filter(n => n.status === 'draft').length;
  const reviewCount = DEMO_NOTES.filter(n => n.status === 'review').length;
  const hccGapCount = 2; // mock

  const toggleNote = (id) => {
    setSelectedNotes(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedNotes(prev => prev.size === filteredNotes.length ? new Set() : new Set(filteredNotes.map(n => n.id)));
  };

  // Cross-app sync
  const { sendToBilling: syncSendToBilling } = useAppSync();

  const handleBatchAction = (action) => {
    if (action === 'send') {
      // Cross-app sync: send selected notes to RevQ billing
      selectedNotes.forEach(noteId => {
        const note = DEMO_NOTES.find(n => n.id === noteId);
        if (note) {
          const patientId = !isDemo ? null : note.patient === 'John Smith' ? 'P001' : note.patient === 'Maria Garcia' ? 'P002' : note.patient === 'Robert Johnson' ? 'P003' : 'P004';
          if (patientId) syncSendToBilling(patientId, { provider: note.provider, cpt: note.cpt || ['99214'], icd: note.icd || ['E11.9'] });
        }
      });
    }
    toast.success(`${selectedNotes.size} notes ${action === 'finalize' ? 'finalized' : 'signed & sent to billing'}`);
    setSelectedNotes(new Set());
    setShowConfirmBatch(null);
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      if (activeFilter === 'referral') setGeneratedText(REFERRAL_TEMPLATE(genSpecialty, genReason));
      else if (activeFilter === 'prior-auth') setGeneratedText(PRIOR_AUTH_TEMPLATE(genProcedure, genDiagnosis));
      else if (activeFilter === 'procedure') setGeneratedText(PROCEDURE_TEMPLATES[genTemplate] || '');
      setIsGenerating(false);
      toast.success('Document generated');
    }, 800);
  };

  const resetGenerator = () => { setShowGenerator(false); setGeneratedText(''); setGenSpecialty(''); setGenReason(''); setGenProcedure(''); setGenDiagnosis(''); setGenTemplate(''); };

  return (
    <div className="space-y-4" data-testid="documentation-page">
      {/* MA Read-Only Banner */}
      {isMA && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="ma-readonly-banner">
          <Eye className="h-4 w-4 text-amber-500 shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-amber-700">View Only — Medical Assistant Mode</p>
            <p className="text-[11px] text-amber-600">You can view encounter notes but cannot edit, sign, or generate documents. Contact your provider for changes.</p>
          </div>
          <Badge className="bg-amber-100 text-amber-700 text-[10px] border-0 shrink-0">Read-Only</Badge>
        </div>
      )}

      {/* Pre-Visit Prep Progress (MA mode) */}
      {isMA && (
        <Card className="border-slate-200" data-testid="prep-progress-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ListChecks className="h-4 w-4 text-slate-500" />
              <p className="text-sm font-semibold text-slate-800">Pre-Visit Prep Status</p>
              <Badge className="text-[10px] bg-amber-50 text-amber-700 border-0 ml-auto">Docs needed for SOAP</Badge>
            </div>
            <div className="space-y-2">
              {(isDemo ? [
                { patient: 'John Smith', total: 4, done: 2, missing: ['Prior visit note', 'Lab results'], noteStatus: 'draft' },
                { patient: 'Maria Garcia', total: 4, done: 1, missing: ['Vitals', 'New patient forms', 'Insurance card'], noteStatus: 'draft' },
                { patient: 'Robert Johnson', total: 4, done: 0, missing: ['Intake forms', 'Vitals', 'Clinical docs', 'Referral doc'], noteStatus: 'review' },
              ] : []).map((p, i) => {
                const pct = Math.round((p.done / p.total) * 100);
                return (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" data-testid={`prep-status-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-slate-800">{p.patient}</span>
                        <Badge className={cn("text-[9px] border-0", STATUS_BADGE[p.noteStatus]?.cls)}>{STATUS_BADGE[p.noteStatus]?.label}</Badge>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" style={{ maxWidth: 200 }}>
                        <div className={cn("h-full rounded-full", pct === 100 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-400" : "bg-red-400")} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] font-medium text-slate-600">{p.done}/{p.total} docs</p>
                      {p.missing.length > 0 && <p className="text-[10px] text-red-500">Missing: {p.missing.join(', ')}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Encounter Review</h1>
          <p className="text-slate-500 mt-1">Clinical notes, referrals, authorizations, and procedure records</p>
        </div>
        {!isMA && (
          <Button onClick={() => setShowGenerator(true)} className="bg-black hover:bg-gray-800 gap-2" data-testid="new-document-btn">
            <Sparkles className="h-4 w-4" /> New Document
          </Button>
        )}
      </div>

      {/* AI Workflow Strip */}
      <div className="bg-slate-900 rounded-xl px-4 py-2.5 flex items-center gap-4 flex-wrap" data-testid="ai-workflow-strip">
        <Sparkles className="h-3.5 w-3.5 text-violet-400 shrink-0" />
        <button onClick={() => setStatusFilter('draft')} className="text-xs text-white/90 hover:text-white transition-colors" data-testid="strip-drafts">
          <span className="font-bold text-amber-400">{draftCount}</span> notes ready to finalize
        </button>
        <div className="h-3 w-px bg-white/20" />
        <button onClick={() => setStatusFilter('review')} className="text-xs text-white/90 hover:text-white transition-colors" data-testid="strip-hcc">
          <span className="font-bold text-amber-400">{hccGapCount}</span> HCC coding gaps across today's visits
        </button>
        <div className="h-3 w-px bg-white/20" />
        <button onClick={() => setStatusFilter('review')} className="text-xs text-white/90 hover:text-white transition-colors" data-testid="strip-review">
          <span className="font-bold text-blue-400">{reviewCount}</span> note flagged for review
        </button>
      </div>

      {/* Filter Tabs - Documentation */}
      <div className="flex gap-2" data-testid="doc-filter-tabs">
        {FILTER_TABS.filter(t => !['capture','sessions','commands'].includes(t.key)).map(tab => {
          const Icon = tab.icon;
          const isActive = activeFilter === tab.key;
          return (
            <button key={tab.key} onClick={() => { setActiveFilter(tab.key); setSearchTerm(''); setSelectedNotes(new Set()); }}
              className={cn("flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 transition-all text-left flex-1",
                isActive ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300 bg-white")}
              data-testid={`doc-tab-${tab.key}`}>
              <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0",
                tab.key === 'soap' ? "bg-violet-100" : tab.key === 'referral' ? "bg-blue-100" : tab.key === 'prior-auth' ? "bg-amber-100" : "bg-slate-100")}>
                <Icon className={cn("h-4 w-4", tab.key === 'soap' ? "text-violet-600" : tab.key === 'referral' ? "text-blue-600" : tab.key === 'prior-auth' ? "text-amber-600" : "text-slate-600")} />
              </div>
              <div className="min-w-0">
                <p className={cn("text-sm font-semibold", isActive ? "text-slate-900" : "text-slate-600")}>{tab.label}</p>
                <p className="text-[11px] text-muted-foreground hidden lg:block">{tab.desc}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter Tabs - Capture & Tools */}
      <div className="flex gap-2" data-testid="tools-filter-tabs">
        {FILTER_TABS.filter(t => ['capture','sessions','commands'].includes(t.key)).map(tab => {
          const Icon = tab.icon;
          const isActive = activeFilter === tab.key;
          return (
            <button key={tab.key} onClick={() => { setActiveFilter(tab.key); setSearchTerm(''); setSelectedNotes(new Set()); }}
              className={cn("flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 transition-all text-left flex-1",
                isActive ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300 bg-white")}
              data-testid={`doc-tab-${tab.key}`}>
              <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0",
                tab.key === 'capture' ? "bg-violet-100" : tab.key === 'sessions' ? "bg-emerald-100" : "bg-slate-800")}>
                <Icon className={cn("h-4 w-4", tab.key === 'capture' ? "text-violet-600" : tab.key === 'sessions' ? "text-emerald-600" : "text-violet-300")} />
              </div>
              <div className="min-w-0">
                <p className={cn("text-sm font-semibold", isActive ? "text-slate-900" : "text-slate-600")}>{tab.label}</p>
                <p className="text-[11px] text-muted-foreground hidden lg:block">{tab.desc}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* SOAP Notes Tab with Filters and Batch Actions */}
      {activeFilter === 'soap' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="space-y-3">
              {/* Robust Filter Bar */}
              <div className="flex items-center gap-2 flex-wrap" data-testid="soap-filter-bar">
                {/* Status Pills */}
                <div className="flex gap-1">
                  {[{ key: 'all', label: 'All' }, { key: 'draft', label: 'Draft' }, { key: 'finalized', label: 'Finalized' }, { key: 'completed', label: 'Signed' }, { key: 'review', label: 'Review' }].map(s => (
                    <Button key={s.key} variant={statusFilter === s.key ? 'default' : 'outline'} size="sm"
                      onClick={() => setStatusFilter(s.key)}
                      className={cn("text-[11px] h-7 px-2.5", statusFilter === s.key && "bg-slate-900")}
                      data-testid={`status-${s.key}`}>
                      {s.label}
                    </Button>
                  ))}
                </div>
                <div className="h-5 w-px bg-slate-200" />
                {/* Date Range Picker */}
                <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant={dateRange.from ? 'default' : 'outline'} size="sm"
                      className={cn("text-[11px] h-7 px-2.5 gap-1.5", dateRange.from && "bg-slate-900")}
                      data-testid="date-range-trigger">
                      <CalendarDays className="h-3 w-3" />
                      {dateRange.from ? (
                        <span className="flex items-center gap-1">
                          {dateRange.from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          {dateRange.to ? ` – ${dateRange.to.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                          <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); e.preventDefault(); setDateRange({ from: undefined, to: undefined }); }}
                            className="ml-0.5 hover:bg-white/20 rounded-full p-0.5 cursor-pointer inline-flex" data-testid="date-range-clear-inline">
                            <X className="h-2.5 w-2.5" />
                          </span>
                        </span>
                      ) : 'Date range'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" sideOffset={6} data-testid="date-range-popover">
                    <div className="p-3 space-y-3">
                      {/* Quick-select pills */}
                      <div className="flex gap-1.5 flex-wrap" data-testid="date-quick-pills">
                        {[
                          { label: 'Today', getRange: () => { const d = new Date(); d.setHours(0,0,0,0); return { from: d, to: d }; }},
                          { label: 'This week', getRange: () => { const now = new Date(); const day = now.getDay(); const start = new Date(now); start.setDate(now.getDate() - day); start.setHours(0,0,0,0); const end = new Date(now); end.setHours(0,0,0,0); return { from: start, to: end }; }},
                          { label: 'This month', getRange: () => { const now = new Date(); const start = new Date(now.getFullYear(), now.getMonth(), 1); const end = new Date(now); end.setHours(0,0,0,0); return { from: start, to: end }; }},
                          { label: 'Last 30 days', getRange: () => { const end = new Date(); end.setHours(0,0,0,0); const start = new Date(end); start.setDate(end.getDate() - 30); return { from: start, to: end }; }},
                          { label: 'Custom', getRange: null },
                        ].map(pill => (
                          <button key={pill.label} onClick={() => {
                            if (pill.getRange) {
                              setDateRange(pill.getRange());
                              setDatePopoverOpen(false);
                            }
                          }}
                            className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors font-medium"
                            data-testid={`date-pill-${pill.label.toLowerCase().replace(/\s/g, '-')}`}>
                            {pill.label}
                          </button>
                        ))}
                      </div>
                      {/* Dual-month calendar */}
                      <Calendar
                        mode="range"
                        selected={dateRange}
                        onSelect={(range) => { if (range) setDateRange(range); }}
                        numberOfMonths={2}
                        classNames={{
                          day_selected: "bg-black text-white hover:bg-black hover:text-white focus:bg-black focus:text-white rounded-full",
                          day_range_start: "day-range-start bg-black text-white rounded-full",
                          day_range_end: "day-range-end bg-black text-white rounded-full",
                          day_range_middle: "aria-selected:bg-[#f0f0f0] aria-selected:text-slate-900",
                          day_today: "underline underline-offset-2 decoration-slate-400 font-semibold",
                          day: "h-8 w-8 p-0 font-normal text-[12px] rounded-full hover:bg-[#f5f5f5] aria-selected:opacity-100",
                          head_cell: "text-[11px] text-muted-foreground font-normal w-8",
                          caption_label: "text-[13px] font-semibold",
                        }}
                        data-testid="date-range-calendar"
                      />
                      {/* From / To inputs */}
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="text-[10px] text-slate-400 font-medium mb-0.5 block">From</label>
                          <div className="text-xs text-slate-700 px-2.5 py-1.5 border border-slate-200 rounded-md bg-slate-50">
                            {dateRange.from ? dateRange.from.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—'}
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] text-slate-400 font-medium mb-0.5 block">To</label>
                          <div className="text-xs text-slate-700 px-2.5 py-1.5 border border-slate-200 rounded-md bg-slate-50">
                            {dateRange.to ? dateRange.to.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—'}
                          </div>
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center justify-end gap-2 pt-1">
                        <Button variant="ghost" size="sm" className="text-[11px] h-7 px-3 text-slate-500"
                          onClick={() => { setDateRange({ from: undefined, to: undefined }); }}
                          data-testid="date-range-clear-btn">
                          Clear
                        </Button>
                        <Button size="sm" className="text-[11px] h-7 px-4 bg-black hover:bg-gray-800"
                          onClick={() => setDatePopoverOpen(false)}
                          data-testid="date-range-apply-btn">
                          Apply
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <div className="h-5 w-px bg-slate-200" />
                {/* Provider Filter */}
                <Select value={providerFilter} onValueChange={setProviderFilter}>
                  <SelectTrigger className="w-40 h-7 text-[11px]" data-testid="provider-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROVIDERS.map(p => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}</SelectContent>
                </Select>
                {/* Search */}
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input placeholder="Search by patient, diagnosis..." className="pl-8 h-7 text-xs" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="soap-search" />
                </div>
              </div>

              {/* Batch Action Bar */}
              {selectedNotes.size > 0 && !isMA && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 border border-slate-200" data-testid="batch-action-bar">
                  <CheckCircle2 className="h-3.5 w-3.5 text-slate-600" />
                  <p className="text-xs font-semibold text-slate-700">{selectedNotes.size} selected</p>
                  <div className="flex-1" />
                  <Button size="sm" className="h-7 text-[10px] gap-1 bg-slate-700 text-white hover:bg-slate-800" onClick={() => setShowConfirmBatch('finalize')} data-testid="batch-finalize-btn">
                    <BookCheck className="h-3 w-3" /> Finalize selected
                  </Button>
                  <Button size="sm" className="h-7 text-[10px] gap-1 bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => setShowConfirmBatch('sign')} data-testid="batch-sign-btn">
                    <Send className="h-3 w-3" /> Sign & send ({selectedNotes.size})
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Select All */}
            {!isMA && (
            <div className="px-6 py-2 border-b border-slate-100 flex items-center gap-3">
              <input type="checkbox" checked={selectedNotes.size === filteredNotes.length && filteredNotes.length > 0} onChange={toggleSelectAll}
                className="h-3.5 w-3.5 rounded border-slate-300" data-testid="select-all-checkbox" />
              <p className="text-[11px] text-slate-400 font-medium">Select all ({filteredNotes.length} notes)</p>
            </div>
            )}
            <div className="divide-y">
              {filteredNotes.map(note => (
                <div key={note.id} className="flex items-center gap-3 px-6 py-3.5 hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`note-${note.id}`}>
                  {!isMA && <input type="checkbox" checked={selectedNotes.has(note.id)} onChange={() => toggleNote(note.id)} onClick={e => e.stopPropagation()}
                    className="h-3.5 w-3.5 rounded border-slate-300 shrink-0" data-testid={`check-${note.id}`} />}
                  <div className="flex items-center gap-4 flex-1 min-w-0" onClick={() => setReviewNote(note)}>
                    <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <FileText className="h-4 w-4 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{note.patient}</p>
                      <p className="text-xs text-muted-foreground">{note.date} &middot; {note.type} &middot; {note.provider}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {note.signed && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] gap-1"><CheckCircle2 className="h-2.5 w-2.5" /> Signed</Badge>}
                      <Badge className={STATUS_BADGE[note.status]?.cls || 'bg-slate-100'} data-testid={`badge-${note.id}`}>{STATUS_BADGE[note.status]?.label}</Badge>
                      <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                    </div>
                  </div>
                </div>
              ))}
              {filteredNotes.length === 0 && (
                <div className="py-12 text-center text-sm text-slate-400">No notes match your filters</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Referral Letters Tab */}
      {activeFilter === 'referral' && (
        <Card>
          <CardHeader><CardTitle className="text-base font-semibold">Referral Letters ({REFERRAL_HISTORY.length})</CardTitle></CardHeader>
          <CardContent className="p-0"><div className="divide-y">
            {REFERRAL_HISTORY.map(ref => (
              <div key={ref.id} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 cursor-pointer" data-testid={`referral-${ref.id}`}>
                <div className="flex items-center gap-4"><div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center"><Send className="h-5 w-5 text-blue-500" /></div><div><p className="text-sm font-medium">{ref.patient}</p><p className="text-xs text-muted-foreground">{ref.date} &middot; {ref.specialty}</p></div></div>
                <Badge className={STATUS_BADGE[ref.status]?.cls}>{STATUS_BADGE[ref.status]?.label}</Badge>
              </div>
            ))}
          </div></CardContent>
        </Card>
      )}

      {/* Prior Auth Tab */}
      {activeFilter === 'prior-auth' && (
        <Card>
          <CardHeader><CardTitle className="text-base font-semibold">Prior Authorizations ({PRIOR_AUTH_HISTORY.length})</CardTitle></CardHeader>
          <CardContent className="p-0"><div className="divide-y">
            {PRIOR_AUTH_HISTORY.map(pa => (
              <div key={pa.id} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 cursor-pointer" data-testid={`prior-auth-${pa.id}`}>
                <div className="flex items-center gap-4"><div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center"><ClipboardCopy className="h-5 w-5 text-amber-500" /></div><div><p className="text-sm font-medium">{pa.patient}</p><p className="text-xs text-muted-foreground">{pa.date} &middot; {pa.procedure}</p></div></div>
                <Badge className={STATUS_BADGE[pa.status]?.cls}>{STATUS_BADGE[pa.status]?.label}</Badge>
              </div>
            ))}
          </div></CardContent>
        </Card>
      )}

      {/* Procedure Notes Tab */}
      {activeFilter === 'procedure' && (
        <Card>
          <CardHeader><CardTitle className="text-base font-semibold">Procedure Notes ({PROCEDURE_NOTE_HISTORY.length})</CardTitle></CardHeader>
          <CardContent className="p-0"><div className="divide-y">
            {PROCEDURE_NOTE_HISTORY.map(pn => (
              <div key={pn.id} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 cursor-pointer" data-testid={`procedure-${pn.id}`}>
                <div className="flex items-center gap-4"><div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center"><Wand2 className="h-5 w-5 text-slate-500" /></div><div><p className="text-sm font-medium">{pn.patient}</p><p className="text-xs text-muted-foreground">{pn.date} &middot; {pn.procedure}</p></div></div>
                <Badge className={STATUS_BADGE[pn.status]?.cls}>{STATUS_BADGE[pn.status]?.label}</Badge>
              </div>
            ))}
          </div></CardContent>
        </Card>
      )}

      {/* Capture Tools Tab */}
      {activeFilter === 'capture' && (
        <div className="space-y-4" data-testid="capture-tools-section">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Start a new capture session or scan documents with Lens</p>
            <Button className="bg-black hover:bg-gray-800 gap-2" onClick={() => handleNewSession('ambient')} data-testid="capture-new-session-btn">
              <Play className="h-4 w-4" /> New Session
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="hover:shadow-md transition-shadow cursor-pointer group" onClick={() => handleNewSession('ambient')} data-testid="tool-ambient">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-xl bg-violet-50 flex items-center justify-center mb-4 group-hover:bg-violet-100 transition-colors">
                  <Mic className="h-6 w-6 text-violet-600" />
                </div>
                <p className="text-base font-bold text-slate-900 mb-1">Ambient Listening</p>
                <p className="text-sm text-slate-500 leading-relaxed">Lumina listens to your patient conversation and generates a structured SOAP note automatically.</p>
                <div className="flex items-center gap-2 mt-3">
                  <Badge className="text-[9px] bg-violet-50 text-violet-600 border-0">Echo-Powered</Badge>
                  <Badge className="text-[9px] bg-slate-100 text-slate-500 border-0">1 credit/min</Badge>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow cursor-pointer group" onClick={() => handleNewSession('dictation')} data-testid="tool-dictation">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                  <MessageSquare className="h-6 w-6 text-blue-600" />
                </div>
                <p className="text-base font-bold text-slate-900 mb-1">Dictation</p>
                <p className="text-sm text-slate-500 leading-relaxed">Dictate your clinical notes directly. Lumina transcribes and structures into SOAP format in real-time.</p>
                <div className="flex items-center gap-2 mt-3">
                  <Badge className="text-[9px] bg-blue-50 text-blue-600 border-0">Voice-to-Text</Badge>
                  <Badge className="text-[9px] bg-slate-100 text-slate-500 border-0">1 credit/min</Badge>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow cursor-pointer group" onClick={() => toast.info('Select a document to analyze with Lens')} data-testid="tool-lens">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-xl bg-amber-50 flex items-center justify-center mb-4 group-hover:bg-amber-100 transition-colors">
                  <ScanLine className="h-6 w-6 text-amber-600" />
                </div>
                <p className="text-base font-bold text-slate-900 mb-1">Lumina Lens</p>
                <p className="text-sm text-slate-500 leading-relaxed">Upload or photograph clinical documents. Lens extracts medications, vitals, diagnoses, and structures data.</p>
                <div className="flex items-center gap-2 mt-3">
                  <Badge className="text-[9px] bg-amber-50 text-amber-600 border-0">OCR + AI</Badge>
                  <Badge className="text-[9px] bg-slate-100 text-slate-500 border-0">1 credit/page</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Session History Tab */}
      {activeFilter === 'sessions' && (
        <Card data-testid="session-history-section">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex gap-1">
                {[{ key: 'all', label: 'All' }, { key: 'note_generated', label: 'Note Generated' }, { key: 'needs_review', label: 'Needs Review' }].map(f => (
                  <Button key={f.key} variant={sessionFilter === f.key ? 'default' : 'outline'} size="sm"
                    onClick={() => setSessionFilter(f.key)}
                    className={cn("text-[11px] h-7 px-2.5", sessionFilter === f.key && "bg-slate-900")}
                    data-testid={`session-filter-${f.key}`}>
                    {f.label}
                  </Button>
                ))}
              </div>
              <div className="flex-1" />
              <div className="relative min-w-[180px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input placeholder="Search sessions..." className="pl-8 h-7 text-xs" value={sessionSearch} onChange={e => setSessionSearch(e.target.value)} data-testid="session-search" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {filteredSessions.map(rec => (
                <div key={rec.id} className="flex items-center gap-4 px-6 py-3.5 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => setReviewNote(rec)}
                  data-testid={`session-${rec.id}`}>
                  <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0",
                    rec.type === 'Ambient' ? 'bg-violet-50' : 'bg-blue-50')}>
                    {rec.type === 'Ambient' ? <Mic className="h-4 w-4 text-violet-600" /> : <MessageSquare className="h-4 w-4 text-blue-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{rec.patient}</p>
                    <p className="text-xs text-muted-foreground">{rec.date} &middot; {rec.duration} &middot; {rec.chief}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={cn("text-[9px] border-0",
                      rec.status === 'note_generated' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700')}>
                      {rec.status === 'note_generated' ? 'Note Generated' : 'Needs Review'}
                    </Badge>
                    <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                  </div>
                </div>
              ))}
              {filteredSessions.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-400">No sessions match your filter</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Voice Commands Tab */}
      {activeFilter === 'commands' && (
        <Card className="border-2 border-slate-900 bg-slate-900 text-white" data-testid="voice-commands-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-violet-600/20 flex items-center justify-center">
                  <Command className="h-5 w-5 text-violet-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-base">Lumina Voice Commands</CardTitle>
                  <CardDescription className="text-slate-400">Hands-free control during recording sessions</CardDescription>
                </div>
              </div>
              <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white gap-1.5 text-xs"
                onClick={() => {
                  const allCmds = VOICE_COMMANDS.flatMap(c => c.commands.map(cmd => `${cmd.cmd} \u2014 ${cmd.desc}`)).join('\n');
                  navigator.clipboard?.writeText(allCmds);
                  toast.success('Voice commands cheat sheet copied!');
                }} data-testid="copy-cheatsheet-btn">
                <Copy className="h-3 w-3" /> Copy cheat sheet
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {VOICE_COMMANDS.map(cat => (
              <div key={cat.category}>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">{cat.category}</p>
                <div className="space-y-1.5">
                  {cat.commands.map(cmd => (
                    <div key={cmd.cmd} className="flex items-start gap-3 p-2.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                      <div className="flex items-center gap-1.5 shrink-0 min-w-[200px]">
                        <Volume2 className="h-3 w-3 text-violet-400 shrink-0" />
                        <code className="text-xs text-violet-300 font-medium">{cmd.cmd}</code>
                      </div>
                      <p className="text-xs text-slate-400">{cmd.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="pt-3 border-t border-slate-700">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-400" />
                <p className="text-xs text-slate-400">
                  <span className="text-amber-400 font-semibold">Pro tip:</span> Voice commands work during both Ambient Listening and Dictation sessions. Start any command with "Lumina" to activate.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Batch Confirmation Modal */}
      <Dialog open={!!showConfirmBatch} onOpenChange={() => setShowConfirmBatch(null)}>
        <DialogContent className="max-w-sm" data-testid="batch-confirm-modal">
          <DialogHeader>
            <DialogTitle>{showConfirmBatch === 'finalize' ? 'Finalize Notes' : 'Sign & Send to Billing'}</DialogTitle>
            <DialogDescription>This will {showConfirmBatch === 'finalize' ? 'finalize' : 'sign and send to billing'} {selectedNotes.size} selected notes. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmBatch(null)}>Cancel</Button>
            <Button className={cn(showConfirmBatch === 'finalize' ? 'bg-slate-700 hover:bg-slate-800' : 'bg-emerald-600 hover:bg-emerald-700', 'text-white')} onClick={() => handleBatchAction(showConfirmBatch)} data-testid="batch-confirm-btn">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generator Dialog */}
      <Dialog open={showGenerator} onOpenChange={resetGenerator}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="generator-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" /> New Document</DialogTitle>
          </DialogHeader>
          {!generatedText ? (
            <div className="space-y-4">
              {activeFilter === 'soap' && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">To create a new SOAP note, open a patient's chart and start an Ambient Visit or Dictation session.</p>
                  <Button onClick={() => { resetGenerator(); navigate('/lumina/patients'); }} className="bg-black hover:bg-gray-800 gap-2">Go to Patients <ArrowRight className="h-4 w-4" /></Button>
                </div>
              )}
              {activeFilter === 'referral' && (
                <div className="space-y-4">
                  <div className="space-y-2"><Label>Referring Specialty</Label>
                    <Select value={genSpecialty} onValueChange={setGenSpecialty}><SelectTrigger><SelectValue placeholder="Select specialty..." /></SelectTrigger>
                      <SelectContent>{['Cardiology', 'Endocrinology', 'Nephrology', 'Neurology', 'Ophthalmology'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-2"><Label>Reason</Label><Textarea placeholder="e.g., Evaluation of persistent headaches..." value={genReason} onChange={e => setGenReason(e.target.value)} rows={3} /></div>
                </div>
              )}
              {activeFilter === 'prior-auth' && (
                <div className="space-y-4">
                  <div className="space-y-2"><Label>Procedure</Label><Input placeholder="e.g., MRI Brain" value={genProcedure} onChange={e => setGenProcedure(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Diagnosis</Label><Input placeholder="e.g., Tension-type headache" value={genDiagnosis} onChange={e => setGenDiagnosis(e.target.value)} /></div>
                </div>
              )}
              {activeFilter === 'procedure' && (
                <div className="space-y-2"><Label>Procedure Template</Label>
                  <Select value={genTemplate} onValueChange={setGenTemplate}><SelectTrigger><SelectValue placeholder="Choose..." /></SelectTrigger>
                    <SelectContent>{Object.keys(PROCEDURE_TEMPLATES).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <Badge className="bg-emerald-50 text-emerald-700 text-xs gap-1"><Sparkles className="h-3 w-3" /> AI Generated</Badge>
              <Textarea value={generatedText} onChange={e => setGeneratedText(e.target.value)} className="min-h-[200px] font-mono text-sm" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={resetGenerator}>Close</Button>
            {activeFilter !== 'soap' && !generatedText && <Button className="bg-black hover:bg-gray-800 gap-2" onClick={handleGenerate} disabled={isGenerating}>{isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate</Button>}
            {generatedText && <Button className="bg-black hover:bg-gray-800 gap-2" onClick={() => { toast.success('Document saved'); resetGenerator(); }}><FileText className="h-4 w-4" /> Save</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NoteReviewLightbox note={reviewNote} open={!!reviewNote} onClose={() => setReviewNote(null)} />
    </div>
  );
};

export default LuminaDocumentation;
